import React, { useState, useRef, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import { cn } from '@/utils/cn'

interface TooltipProps {
  content: ReactNode
  children?: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'top',
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const timeoutRef = useRef<number>()
  const triggerRef = useRef<HTMLDivElement>(null)

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const scrollY = window.scrollY
      const scrollX = window.scrollX
      
      let x = 0
      let y = 0
      
      switch (position) {
        case 'top':
          x = rect.left + rect.width / 2 + scrollX
          y = rect.top + scrollY - 8
          break
        case 'bottom':
          x = rect.left + rect.width / 2 + scrollX
          y = rect.bottom + scrollY + 8
          break
        case 'left':
          x = rect.left + scrollX - 8
          y = rect.top + rect.height / 2 + scrollY
          break
        case 'right':
          x = rect.right + scrollX + 8
          y = rect.top + rect.height / 2 + scrollY
          break
      }
      
      setTooltipPosition({ x, y })
    }
    
    setIsVisible(true)
  }

  const hideTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 150) // Small delay to prevent flashing
  }

  const getTransformOrigin = () => {
    switch (position) {
      case 'top':
        return 'translate(-50%, -100%)'
      case 'bottom':
        return 'translate(-50%, 0%)'
      case 'left':
        return 'translate(-100%, -50%)'
      case 'right':
        return 'translate(0%, -50%)'
      default:
        return 'translate(-50%, -100%)'
    }
  }

  const renderTooltip = () => {
    if (!isVisible) return null

    return createPortal(
      <div 
        className="fixed z-[9999] px-3 py-2 text-xs bg-gray-800 text-white rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          transform: getTransformOrigin(),
          zIndex: 9999
        }}
      >
        {content}
      </div>,
      document.body
    )
  }

  return (
    <div 
      ref={triggerRef}
      className={cn("relative inline-block", className)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children || (
        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
      )}
      
      {renderTooltip()}
    </div>
  )
}

export default Tooltip